import { TagCombobox } from "components/TagCombobox/TagCombobox";
import { useToast } from "hooks/useToast";
import {
  useAddTagsMutation,
  useCreateTaskTagMutation,
  useRemoveTagsMutation,
} from "store/apis/taskTags";
import { BackendError } from "types/utils/error";
import { TaskTags } from "types/Models/task";
import { MultiValue, SingleValue } from "react-select";
import { OptionData } from "components/DesignSystem/Combobox/Combobox";
import { useRoleBasedView } from "hooks/useRoleBasedView";

export const FilingTags = ({
  taskId,
  currentTags,
  tags,
  size = "small",
}: {
  taskId: string;
  currentTags: TaskTags[] | undefined;
  tags: TaskTags[];
  size?: "small" | "regular" | "large";
}) => {
  const { alertToast } = useToast();
  const { isAdmin } = useRoleBasedView();
  const [addTags, { isLoading: isAdding }] = useAddTagsMutation();
  const [removeTags, { isLoading: isRemoving }] = useRemoveTagsMutation();
  const [createTaskTag, { isLoading: isCreating }] = useCreateTaskTagMutation();

  const isLoading = Boolean(isAdding || isRemoving || isCreating);

  const onTagsChange = async (
    selectedTags: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    const selectedValues = Array.isArray(selectedTags)
      ? selectedTags
      : selectedTags
      ? [selectedTags]
      : [];

    const addedTag = selectedValues.filter(
      ({ value }) => !currentTags?.some(({ uuid }) => uuid === value)
    )?.[0];

    const removedTag = currentTags?.filter(
      ({ uuid }) => !selectedValues?.some(({ value }) => uuid === value)
    )?.[0];

    try {
      if (removedTag) {
        await removeTags({ taskId, tagId: removedTag?.uuid }).unwrap();
      } else {
        await addTags({ taskId, tagId: addedTag.value }).unwrap();
      }
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  // Type a new tag name in the dropdown to create it and apply it to this filing
  const onTagCreate = async (title: string) => {
    try {
      const newTag = await createTaskTag({ title }).unwrap();
      await addTags({ taskId, tagId: newTag.uuid }).unwrap();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <TagCombobox
      tags={tags}
      currentTags={currentTags}
      isLoading={isLoading}
      onTagsChange={onTagsChange}
      size={size}
      {...(isAdmin ? { creatable: true, onCreateOption: onTagCreate } : {})}
    />
  );
};
